import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const AccountSettings = () => {
  const { currentUser, sendCustomVerificationEmail, checkEmailVerification } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  const [accountData, setAccountData] = useState({
    email: '',
    phoneNumber: '',
    profilePicture: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [editingSection, setEditingSection] = useState(null);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState({
    isVerified: false,
    loading: false
  });

  useEffect(() => {
    fetchProfile();
    fetchEmailVerificationStatus();
  }, [currentUser]);

  const fetchProfile = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const profileData = docSnap.data();
        setProfile(profileData);
        setAccountData({
          email: currentUser.email || '',
          phoneNumber: profileData.phoneNumber || '',
          profilePicture: profileData.profilePicture || currentUser.photoURL || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load account information');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailVerificationStatus = async () => {
    try {
      setEmailVerificationStatus(prev => ({ ...prev, loading: true }));
      
      // Only use our custom verification status from Firestore
      try {
        const result = await checkEmailVerification();
        setEmailVerificationStatus({
          isVerified: result.isVerified, // Only use our custom verification, no fallback to Firebase
          loading: false
        });
      } catch (error) {
        console.error('Error checking email verification:', error);
        // If there's an error checking, default to not verified
        setEmailVerificationStatus({
          isVerified: false,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error fetching email verification status:', error);
      setEmailVerificationStatus({
        isVerified: false,
        loading: false
      });
    }
  };

  const handleResendVerificationEmail = async () => {
    try {
      setEmailVerificationStatus(prev => ({ ...prev, loading: true }));
      
      await sendCustomVerificationEmail(true);
      setMessage('Verification email sent! Please check your inbox.');
      
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError('Failed to send verification email. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setEmailVerificationStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAccountUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    
    try {
      // Update Firestore document
      const docRef = doc(db, 'users', currentUser.uid);
      await updateDoc(docRef, {
        phoneNumber: accountData.phoneNumber,
        updatedAt: new Date()
      });

      setMessage('Phone number updated successfully!');
      setEditingSection(null);
      await fetchProfile(); // Refresh data
    } catch (error) {
      console.error('Error updating account:', error);
      setError(error.message || 'Failed to update account information');
    } finally {
      setSaving(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (password.length > 20) {
      errors.push('Password must be no more than 20 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    return errors;
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    if (!passwordData.currentPassword) {
      setError('Please enter your current password');
      setSaving(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setSaving(false);
      return;
    }

    const passwordErrors = validatePassword(passwordData.newPassword);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('. '));
      setSaving(false);
      return;
    }

    try {
      // Reauthenticate user with current password
      const credential = EmailAuthProvider.credential(
        currentUser.email, 
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, passwordData.newPassword);
      
      setMessage('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setEditingSection(null);
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setError('The current password you entered is incorrect. Please try again.');
      } else if (error.code === 'auth/weak-password') {
        setError('The new password is too weak. Please choose a stronger password.');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('For security reasons, please log out and log back in before changing your password.');
      } else {
        setError(error.message || 'Failed to update password. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'phoneNumber') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      // Limit to 10 digits
      const limitedDigits = digitsOnly.slice(0, 10);
      // Format as (XXX) XXX-XXXX
      let formattedValue = limitedDigits;
      if (limitedDigits.length >= 6) {
        formattedValue = `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
      } else if (limitedDigits.length >= 3) {
        formattedValue = `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
      }
      
      setAccountData(prev => ({
        ...prev,
        [field]: formattedValue
      }));
    } else {
      setAccountData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError('Image size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
    
    setError('');
  };

  const handleUploadPhoto = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // For now, convert to base64 data URL as a fallback
      // This avoids Firebase Storage CORS issues during development
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const dataURL = e.target.result;
          
          // Update Firestore with the data URL
          const docRef = doc(db, 'users', currentUser.uid);
          await updateDoc(docRef, {
            profilePicture: dataURL,
            updatedAt: new Date()
          });

          // Update local state
          setAccountData(prev => ({
            ...prev,
            profilePicture: dataURL
          }));

          setMessage('Profile picture updated successfully!');
          setSelectedFile(null);
          setPreviewUrl(null);
          setEditingSection(null);
          
          // Clear the file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }

          await fetchProfile(); // Refresh profile data
          setUploading(false);
        } catch (error) {
          console.error('Error saving profile picture:', error);
          setError('Failed to save profile picture. Please try again.');
          setUploading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read the selected file. Please try again.');
        setUploading(false);
      };

      reader.readAsDataURL(selectedFile);

      // TODO: For production, use Firebase Storage upload:
      /*
      const fileExtension = selectedFile.name.split('.').pop();
      const fileName = `profile-pictures/${currentUser.uid}-${Date.now()}.${fileExtension}`;
      
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      const docRef = doc(db, 'users', currentUser.uid);
      await updateDoc(docRef, {
        profilePicture: downloadURL,
        updatedAt: new Date()
      });
      */

    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setError('Failed to upload profile picture. Please try again.');
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!accountData.profilePicture) return;

    setUploading(true);
    setError('');

    try {
      // Remove from Firestore
      const docRef = doc(db, 'users', currentUser.uid);
      await updateDoc(docRef, {
        profilePicture: '',
        updatedAt: new Date()
      });

      // Update local state
      setAccountData(prev => ({
        ...prev,
        profilePicture: ''
      }));

      setMessage('Profile picture removed successfully!');
      setEditingSection(null);
      await fetchProfile(); // Refresh profile data
    } catch (error) {
      console.error('Error removing profile picture:', error);
      setError('Failed to remove profile picture. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading account settings...</div>;
  }

  return (
    <div className="account-settings">
      <div className="account-container">
        <div className="account-header">
          <h2>My Account</h2>
          <p>Manage your account information and security settings</p>
        </div>

        {message && (
          <div className="success-message">
            {message}
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Profile Picture Section */}
        <div className="account-section">
          <div className="section-header">
            <h3>Profile Picture</h3>
            <button 
              onClick={() => setEditingSection(editingSection === 'photo' ? null : 'photo')}
              className="edit-section-btn"
            >
              {editingSection === 'photo' ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="profile-picture-section">
            <div className="current-picture">
              {accountData.profilePicture ? (
                <img 
                  src={accountData.profilePicture} 
                  alt="Profile" 
                  className="profile-picture-preview"
                />
              ) : (
                <div className="default-picture">
                  {profile?.firstName?.[0] || currentUser?.email?.[0] || '?'}
                </div>
              )}
            </div>

            {editingSection === 'photo' ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Upload Profile Picture</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="file-input"
                  />
                  <small>Choose a JPEG, PNG, GIF, or WebP image (max 5MB)</small>
                </div>

                {previewUrl && (
                  <div className="image-preview">
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      className="preview-image-large"
                    />
                    <p>Preview of your new profile picture</p>
                  </div>
                )}

                <div className="form-actions">
                  {selectedFile && (
                    <button 
                      type="button" 
                      onClick={handleUploadPhoto} 
                      disabled={uploading} 
                      className="save-btn"
                    >
                      {uploading ? 'Uploading...' : 'Upload Photo'}
                    </button>
                  )}
                  
                  {accountData.profilePicture && (
                    <button 
                      type="button" 
                      onClick={handleRemovePhoto} 
                      disabled={uploading} 
                      className="remove-photo-btn"
                    >
                      {uploading ? 'Removing...' : 'Remove Current Photo'}
                    </button>
                  )}
                  
                  <button 
                    type="button" 
                    onClick={() => {
                      setEditingSection(null);
                      setSelectedFile(null);
                      setPreviewUrl(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="picture-info">
                <p>Click "Edit" to update your profile picture</p>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="account-section">
          <div className="section-header">
            <h3>Contact Information</h3>
            <button 
              onClick={() => setEditingSection(editingSection === 'contact' ? null : 'contact')}
              className="edit-section-btn"
            >
              {editingSection === 'contact' ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {editingSection === 'contact' ? (
            <form onSubmit={handleAccountUpdate} className="edit-form">
              <div className="form-group">
                <label>Phone Number (Optional)</label>
                <input
                  type="tel"
                  value={accountData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="(555) 123-4567"
                  maxLength="14"
                />
                <small>This will not be shown publicly</small>
              </div>

              <div className="form-actions">
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? 'Saving...' : 'Save Phone Number'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setEditingSection(null)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="contact-display">
              <div className="info-item">
                <label>Email Address:</label>
                <span>{accountData.email}</span>
              </div>
              <div className="info-item">
                <label>Email Status:</label>
                <div className="email-verification-status">
                  {emailVerificationStatus.loading ? (
                    <span className="status verifying">Checking...</span>
                  ) : emailVerificationStatus.isVerified ? (
                    <span className="status verified">✅ Verified</span>
                  ) : (
                    <div className="unverified-email">
                      <span className="status unverified">❌ Not Verified</span>
                      <button 
                        onClick={handleResendVerificationEmail}
                        disabled={emailVerificationStatus.loading}
                        className="save-btn"
                        style={{ 
                          fontSize: '0.875rem',
                          padding: '8px 16px',
                          background: emailVerificationStatus.loading 
                            ? '#6b7280' 
                            : 'linear-gradient(135deg, #059669, #047857)'
                        }}
                      >
                        {emailVerificationStatus.loading ? 'Sending...' : 'Resend Verification Email'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="info-item">
                <label>Phone Number:</label>
                <span>{accountData.phoneNumber || 'Not provided'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Security Section */}
        <div className="account-section">
          <div className="section-header">
            <h3>Security</h3>
            <button 
              onClick={() => setEditingSection(editingSection === 'password' ? null : 'password')}
              className="edit-section-btn"
            >
              {editingSection === 'password' ? 'Cancel' : 'Change Password'}
            </button>
          </div>

          {editingSection === 'password' ? (
            <form onSubmit={handlePasswordUpdate} className="edit-form">
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  required
                  placeholder="Enter your current password"
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  required
                  minLength="8"
                  maxLength="20"
                  placeholder="Enter your new password"
                />
                
                {/* Password Requirements Display */}
                <div className="password-requirements">
                  <p className="requirements-title">Password must contain:</p>
                  <ul className="requirements-list">
                    <li className={passwordData.newPassword.length >= 8 && passwordData.newPassword.length <= 20 ? 'valid' : 'invalid'}>
                      8-20 characters
                    </li>
                    <li className={/[A-Z]/.test(passwordData.newPassword) ? 'valid' : 'invalid'}>
                      At least one uppercase letter
                    </li>
                    <li className={/[a-z]/.test(passwordData.newPassword) ? 'valid' : 'invalid'}>
                      At least one lowercase letter
                    </li>
                    <li className={/\d/.test(passwordData.newPassword) ? 'valid' : 'invalid'}>
                      At least one number
                    </li>
                  </ul>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  required
                  minLength="8"
                  maxLength="20"
                  placeholder="Confirm your new password"
                />
                {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <small className="validation-error">Passwords do not match</small>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setEditingSection(null)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="security-display">
              <div className="info-item">
                <label>Password:</label>
                <span>••••••••</span>
              </div>
              <p className="security-note">
                Keep your account secure by using a strong password
              </p>
            </div>
          )}
        </div>

        {/* Account Information */}
        <div className="account-section">
          <div className="section-header">
            <h3>Account Information</h3>
          </div>
          <div className="account-info">
            <div className="info-item">
              <label>Account Type:</label>
              <span className="account-type">
                {profile?.userType === 'both' && 'Practitioner & Provider'}
                {profile?.userType === 'provider' && 'Provider'}
                {profile?.userType === 'practitioner' && 'Practitioner'}
                {!profile?.userType && 'Not set'}
              </span>
            </div>
            <div className="info-item">
              <label>Member Since:</label>
              <span>
                {profile?.createdAt ? 
                  new Date(profile.createdAt.seconds * 1000).toLocaleDateString() : 
                  'Unknown'
                }
              </span>
            </div>
            <div className="info-item">
              <label>Profile Complete:</label>
              <span className={`status ${profile?.isProfileComplete ? 'complete' : 'incomplete'}`}>
                {profile?.isProfileComplete ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;